import * as Accordion from "@radix-ui/react-accordion";
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

import { getAllRole } from "../../../services/Role/Role.service";
import {
  getMenuAccessByRole,
  createMenuAccess,
} from "../../../services/menu_access/menu_access.service";

import { Checkbox } from "../../../components/checkbox";
import { toast } from "react-toastify";
import "./AddRoleMenuPermission.css";

/* ================= TYPES ================= */

interface MenuAccess {
  menu_master_id: number;
  view: boolean;        // only this matters now
  add: boolean;
  edit: boolean;
  delete: boolean;
  status: string;
  role_id: number;
}

interface MenuItem {
  menu_master_id: number;
  menu_name: string;
  menu_access?: MenuAccess;
  submenu_master?: MenuItem[];
}

interface Role {
  role_id: number;
  role_name: string;
}

/* ================= COMPONENT ================= */

export default function RoleMenuPermission() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [accessMap, setAccessMap] = useState<Map<number, boolean>>(new Map());

  /* ================= FETCH ROLES ================= */
  useEffect(() => {
    getAllRole().then((res) => setRoles(res.data || []));
  }, []);

  /* ================= FETCH MENUS ================= */
  const fetchMenus = async (roleId: number) => {
    const res = await getMenuAccessByRole(roleId);
    const data: MenuItem[] = res?.data || [];
    setMenus(data);

    // Build access map: true if any permission (we use 'view' as the toggle)
    const map = new Map<number, boolean>();
    const collect = (items: MenuItem[]) => {
      items.forEach((item) => {
        const hasAccess = !!item.menu_access?.view;
        map.set(item.menu_master_id, hasAccess);
        if (item.submenu_master) collect(item.submenu_master);
      });
    };
    collect(data);
    setAccessMap(map);
  };

  /* ================= TOGGLE ACCESS ================= */
  const toggleAccess = (menuId: number) => {
    setAccessMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(menuId, !prev.get(menuId));
      return newMap;
    });
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error("Please select role");
      return;
    }

    // Build payload with view = granted, all other perms false
    const buildPayload = (items: MenuItem[]): any[] => {
      return items.map((item) => ({
        menu_master_id: item.menu_master_id,
        menu_name: item.menu_name,
        menu_access: {
          role_id: selectedRole,
          menu_master_id: item.menu_master_id,
          view: accessMap.get(item.menu_master_id) || false,
          add: false,
          edit: false,
          delete: false,
          status: "active",
        },
        submenu_master: item.submenu_master
          ? buildPayload(item.submenu_master)
          : [],
      }));
    };

    const payload = buildPayload(menus);
    await createMenuAccess(payload);
    toast.success("Menu permissions saved successfully");
  };

  /* ================= RENDER ================= */
  return (
    <div className="rmp-container">
      {/* ROLE SELECT */}
      <div className="role-select-wrapper">
        <select
          value={selectedRole || ""}
          onChange={(e) => {
            const id = Number(e.target.value);
            setSelectedRole(id);
            fetchMenus(id);
          }}
        >
          <option value="">Select Role</option>
          {roles.map((r) => (
            <option key={r.role_id} value={r.role_id}>
              {r.role_name}
            </option>
          ))}
        </select>
      </div>

      {/* MENU LIST */}
      <div className="menu-wrapper">
        <Accordion.Root type="multiple">
          {menus.map((item, index) => (
            <Accordion.Item
              key={item.menu_master_id}
              value={`item-${index}`}
              className="menu-card"
            >
              <Accordion.Header>
                <Accordion.Trigger className="menu-header">
                  <div className="menu-left">
                    <Checkbox
                      checked={accessMap.get(item.menu_master_id) || false}
                      onCheckedChange={() => toggleAccess(item.menu_master_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {item.menu_name}
                  </div>
                  {(item.submenu_master?.length ?? 0) > 0 && (
                    <ChevronDown size={18} />
                  )}
                </Accordion.Trigger>
              </Accordion.Header>

              {item.submenu_master?.length ? (
                <Accordion.Content className="menu-content">
                  <div className="submenu">
                    {item.submenu_master.map((child) => (
                      <div
                        key={child.menu_master_id}
                        className="submenu-item"
                      >
                        <Checkbox
                          checked={accessMap.get(child.menu_master_id) || false}
                          onCheckedChange={() => toggleAccess(child.menu_master_id)}
                        />
                        {child.menu_name}
                      </div>
                    ))}
                  </div>
                </Accordion.Content>
              ) : null}
            </Accordion.Item>
          ))}
        </Accordion.Root>
      </div>

      <button className="submit-btn" onClick={handleSubmit}>
        Save Permissions
      </button>
    </div>
  );
}